import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useId, useState } from "react";
import { SimpleInput } from "@/core/components/ui/simple-input";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type {
  PayrollRosterField,
  RosterBooleanFilter,
  RosterFieldFilter,
  RosterFieldValueSummary,
  RosterOptionsFilter,
  RosterRangeFilter,
  RosterTextFilter,
} from "@/payroll/roster/types";
import { humanizeRosterOption } from "@/payroll/roster/utils";
import {
  formatRosterValue,
  parseRosterRangeBound,
} from "@/payroll/roster/utils";
import {
  FilterCheckRow,
  OPTION_SEARCH_THRESHOLD,
  POPOVER_INPUT_CLASS,
  toggleValue,
} from "./filter-control-parts";

interface FilterBodyProps<TFilter extends RosterFieldFilter> {
  field: PayrollRosterField;
  options: string[];
  summary: RosterFieldValueSummary | undefined;
  filter: TFilter;
  onChange: (filter: RosterFieldFilter) => void;
}

export const OptionsFilterBody = ({
  field,
  options,
  summary,
  filter,
  onChange,
}: FilterBodyProps<RosterOptionsFilter>) => {
  const idPrefix = useId();
  const [search, setSearch] = useState("");
  const query = search.trim().toLocaleLowerCase();
  const visibleOptions = options
    .map((option, index) => ({
      option,
      index,
      label: humanizeRosterOption(option),
    }))
    .filter(
      ({ option, label }) =>
        option.toLocaleLowerCase().includes(query) ||
        label.toLocaleLowerCase().includes(query),
    );

  if (options.length === 0) {
    return (
      <Typography variant="bodyW3" textColor="secondary" className="px-3 py-4">
        This field has no choices to filter by yet.
      </Typography>
    );
  }

  return (
    <Stack gap="none" fullWidth>
      {options.length >= OPTION_SEARCH_THRESHOLD && (
        <Stack
          gap="none"
          fullWidth
          className="border-b px-3 py-2 [&>div]:w-full"
        >
          <SimpleInput
            value={search}
            aria-label={`Search ${field.label} values`}
            placeholder="Search values"
            startAdornment={<MagnifyingGlassIcon className="size-4" />}
            onChange={(event) => setSearch(event.target.value)}
            className={POPOVER_INPUT_CLASS}
          />
        </Stack>
      )}
      <Stack gap="none" fullWidth className="max-h-64 overflow-y-auto p-1">
        {visibleOptions.length === 0 ? (
          <Typography
            variant="bodyW3"
            textColor="secondary"
            className="py-4 text-center"
          >
            No matching values
          </Typography>
        ) : (
          visibleOptions.map(({ option, index, label }) => (
            <FilterCheckRow
              key={option}
              id={`${idPrefix}-${index}`}
              label={label}
              count={summary?.counts[option] ?? 0}
              checked={filter.values.includes(option)}
              onToggle={() =>
                onChange({
                  ...filter,
                  values: toggleValue(filter.values, option),
                })
              }
            />
          ))
        )}
      </Stack>
    </Stack>
  );
};

export const BooleanFilterBody = ({
  field,
  summary,
  filter,
  onChange,
}: FilterBodyProps<RosterBooleanFilter>) => (
  <Stack gap="none" fullWidth className="p-1">
    {[true, false].map((value) => (
      <FilterCheckRow
        key={String(value)}
        id={`roster-filter-${field.key}-${value ? "yes" : "no"}`}
        label={value ? "Yes" : "No"}
        count={summary?.counts[String(value)] ?? 0}
        checked={filter.values.includes(value)}
        onToggle={() =>
          onChange({ ...filter, values: toggleValue(filter.values, value) })
        }
      />
    ))}
  </Stack>
);

export const RangeFilterBody = ({
  field,
  summary,
  filter,
  onChange,
}: FilterBodyProps<RosterRangeFilter>) => {
  const min = parseRosterRangeBound(filter.min);
  const max = parseRosterRangeBound(filter.max);
  const inverted = min !== null && max !== null && min > max;
  const inputs = [
    {
      id: `roster-filter-${field.key}-min`,
      label: "From",
      value: filter.min,
      key: "min",
      placeholder: summary?.min,
    },
    {
      id: `roster-filter-${field.key}-max`,
      label: "To",
      value: filter.max,
      key: "max",
      placeholder: summary?.max,
    },
  ] as const;

  return (
    <Stack gap="sm" fullWidth className="p-3">
      <Stack direction="row" gap="sm" fullWidth>
        {inputs.map(({ id, label, value, key, placeholder }) => (
          <Stack key={key} gap="xs" className="min-w-0 flex-1 [&>div]:w-full">
            <label
              htmlFor={id}
              className="text-xs font-medium text-secondary-text"
            >
              {label}
            </label>
            <SimpleInput
              id={id}
              inputMode="decimal"
              placeholder={placeholder == null ? "Any" : String(placeholder)}
              value={value}
              onChange={(event) =>
                onChange({ ...filter, [key]: event.target.value })
              }
              className={POPOVER_INPUT_CLASS}
            />
          </Stack>
        ))}
      </Stack>
      {inverted ? (
        <Typography variant="bodyW3" className="text-negative-text">
          “From” is higher than “To”, so nobody can match.
        </Typography>
      ) : (
        summary?.min != null &&
        summary.max != null && (
          <Typography variant="bodyW3" textColor="secondary">
            {summary.min === summary.max
              ? `Everyone is at ${formatRosterValue(String(summary.min), field)}`
              : `Roster runs ${formatRosterValue(String(summary.min), field)} to ${formatRosterValue(String(summary.max), field)}`}
          </Typography>
        )
      )}
    </Stack>
  );
};

export const TextFilterBody = ({
  field,
  filter,
  onChange,
}: FilterBodyProps<RosterTextFilter>) => (
  <Stack gap="none" fullWidth className="p-3 [&>div]:w-full">
    <SimpleInput
      autoFocus
      aria-label={`${field.label} contains`}
      placeholder="Contains…"
      value={filter.query}
      onChange={(event) => onChange({ ...filter, query: event.target.value })}
      className={POPOVER_INPUT_CLASS}
    />
  </Stack>
);
